import {
  FlatHierarchicalSelectOption,
  HierarchicalSelectOption,
  ValueMap,
} from './types';

/**
 * Finds the path up to a target in the hierarchical data
 */
export function findPath(
  flatData: FlatHierarchicalSelectOption[],
  targetId: string,
): string[] {
  const path: string[] = [];

  // Helper function to recursively find the path
  function recursiveFind(id: string, currentPath: string[]) {
    const item = flatData.find((option) => option.id === id);
    if (item) {
      currentPath.unshift(item.id); // Add the current item ID to the path
      if (item.parentId) {
        recursiveFind(item.parentId, currentPath); // Recurse upwards to find the parent
      }
    }
  }

  recursiveFind(targetId, path);
  return path;
}

/**
 * Flattens the hierarchical data
 */
export const flattenHierarchicalData = (
  options: HierarchicalSelectOption[],
  path: string[] = [],
  parentId: string | null = null,
): FlatHierarchicalSelectOption[] => {
  let flattened: FlatHierarchicalSelectOption[] = [];

  options.forEach((option) => {
    const flatOption = { ...option, path: [...path, option.id], parentId };
    flattened.push(flatOption);

    // If there are children, recursively flatten them
    if (option.children && option.children.length > 0) {
      flattened = flattened.concat(
        flattenHierarchicalData(
          option.children,
          [...path, option.id],
          option.id,
        ),
      );
    }
  });

  return flattened;
};

/**
 * Updates hierarchical data in a giver path
 */
export function updateHierarchicalSelection(
  valueMap: ValueMap,
  path: string[],
  newSelectedValue: boolean,
) {
  let current = valueMap;

  // Traverse the path
  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i];
    if (!current[key]) {
      current[key] = { selected: newSelectedValue, selection: {} };
    }
    current[key].selected = newSelectedValue;
    current = current[key].selection;
  }

  // Update the selected state of the final key in the path
  const finalKey = path[path.length - 1];
  if (!current[finalKey] && newSelectedValue) {
    current[finalKey] = { selected: newSelectedValue, selection: {} };
  } else if (current[finalKey] && !newSelectedValue) {
    delete current[finalKey];
  } else if (current[finalKey]) {
    current[finalKey].selected = newSelectedValue;
  }

  // Clear any empty parents
  if (!newSelectedValue) {
    current = valueMap;

    for (let i = 0; i < path.length - 1; i += 1) {
      const key = path[i];
      if (!current[key]) {
        break;
      }
      if (Object.keys(current[key].selection).length === 0) {
        delete current[key];
        break;
      }
    }
  }

  return valueMap;
}

/**
 * Finds a value in the hierarchical data
 */
export function findItem(valueMap: ValueMap, path: string[]) {
  let current = valueMap;

  // Traverse the path
  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i];
    if (!current[key]) {
      return null;
    }
    current = current[key].selection;
  }

  // Update the selected state of the final key in the path
  const finalKey = path[path.length - 1];
  if (current[finalKey]) {
    return current[finalKey];
  }

  return null;
}

export function filterHierarchyByLabel(
  data: HierarchicalSelectOption[],
  search: string,
): HierarchicalSelectOption[] {
  return data
    .map((item) => {
      // If the item's label matches, keep it and process its children
      if (item.label.toLocaleLowerCase().includes(search.toLocaleLowerCase())) {
        return {
          ...item,
          children: item.children
            ? filterHierarchyByLabel(item.children, search)
            : [],
        };
      }

      // If the label doesn't match, check its children
      if (item.children) {
        const filteredChildren = filterHierarchyByLabel(item.children, search);
        if (filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }
      }

      // Return null if no match is found
      return null;
    })
    .filter((item) => item !== null) as HierarchicalSelectOption[];
}

/**
 * Keeps only the branches that are selected or contain selected descendants.
 * Used by the "selected" view of the selection filter.
 */
export function filterHierarchyBySelection(
  data: HierarchicalSelectOption[],
  selection: ValueMap,
  flatOptions: FlatHierarchicalSelectOption[],
): HierarchicalSelectOption[] {
  return data
    .map((item) => {
      const path = flatOptions.find((flat) => flat.id === item.id)?.path || [
        item.id,
      ];
      const isSelected = findItem(selection, path)?.selected || false;

      // If the item itself is selected, keep it with its original children
      if (isSelected) {
        return { ...item, children: item.children ?? [] };
      }

      // Otherwise keep it only if it has selected descendants
      if (item.children) {
        const filteredChildren = filterHierarchyBySelection(
          item.children,
          selection,
          flatOptions,
        );
        if (filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }
      }

      return null;
    })
    .filter((item) => item !== null) as HierarchicalSelectOption[];
}
